import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, LogOut, Menu, X, Heart, Package, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import SearchBarNav from './SearchBarNav';

function Navbar() {
  const { user, profile, loading, signOut } = useAuth();
  const { currency, supportedCurrencies, setCurrency } = useCurrency();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    await signOut();
  };

  const profileLink = profile?.username
    ? `/profile/${profile.username}`
    : loading
      ? '#'
      : '/profile-setup';

  const collectionLink = profile?.username
    ? `/profile/${profile.username}?tab=collections`
    : '#';

  const wishlistLink = profile?.username
    ? `/profile/${profile.username}?tab=wishlist`
    : '#';


  const handleCurrencyChange = async (event) => {
    const nextCurrency = event.target.value
    await setCurrency(nextCurrency, { persist: true })
  }

  return (
    <>
      <header className="bg-dark border-b border-purple/30 p-4 flex items-center justify-between relative z-40">
          <div className="flex items-center gap-2">
              <div className="h-8 w-auto">
                  <Link to="/">
                      <img src="/logo.png" alt="Lilycove Logo" className="h-8 w-auto" />
                  </Link>
              </div>
              <SearchBarNav />
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm tracking-wide">
              <Link to="/" className="text-cream hover:text-purple transition">HOME</Link>
              <Link to="/series" className="text-cream hover:text-purple transition">SERIES</Link>
              <Link to="/sealed" className="text-cream hover:text-purple transition" data-testid="nav-sealed">SEALED</Link>
              {/* WO-35: "Report a bug" moved to the footer (founder); the help button bottom-right also opens it. */}
          </nav>

          <div className="flex items-center gap-2">
              <select
                value={currency}
                onChange={handleCurrencyChange}
                className="hidden md:block h-9 rounded-md border border-purple/30 bg-dark px-2 py-1 text-xs text-cream"
                title={user ? 'Saved to your account' : 'Saved on this device'}
              >
                {supportedCurrencies.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              {/* Desktop user actions */}
              <div className="hidden md:flex items-center gap-3">
                {user ? (
                  <>
                    {/* Quick links for logged-in users */}
                    <Link to={collectionLink} className="text-cream/60 hover:text-purple transition-colors" title="My Collection">
                      <Package size={18} />
                    </Link>
                    <Link to={wishlistLink} className="text-cream/60 hover:text-purple transition-colors" title="My Wishlist">
                      <Heart size={18} />
                    </Link>
                    <Link to="/settings" className="text-cream/60 hover:text-purple transition-colors" title="Settings">
                      <Settings size={18} />
                    </Link>
                    <div className="w-px h-5 bg-purple/20 mx-1" />
                    <Link 
                      to={profileLink}
                      className="flex items-center gap-2 text-cream hover:text-purple transition-colors"
                    >
                      {profile?.avatar_url ? (
                        <img 
                          src={profile.avatar_url} 
                          alt="Profile" 
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-purple/20 flex items-center justify-center">
                          <User size={16} />
                        </div>
                      )}
                      <span className="hidden lg:block text-sm">
                        {profile?.display_name || profile?.username || user?.email?.split('@')[0] || 'Profile'}
                      </span>
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 w-9 text-cream hover:text-purple hover:bg-dark/50 transition-colors"
                      title="Sign Out"
                    >
                      <LogOut size={16} />
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/login" 
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium h-9 w-9 text-cream hover:text-purple hover:bg-dark/50"
                    title="Sign In"
                  >
                    <User size={20} />
                  </Link>
                )}
              </div>

              {/* Mobile: profile icon + hamburger */}
              <div className="flex md:hidden items-center gap-2">
                {user && (
                  <Link to={profileLink} className="text-cream hover:text-purple transition-colors">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="Profile" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-purple/20 flex items-center justify-center">
                        <User size={14} />
                      </div>
                    )}
                  </Link>
                )}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="inline-flex items-center justify-center rounded-md h-9 w-9 text-cream hover:text-purple hover:bg-dark/50 transition-colors"
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
              </div>
          </div>
      </header>

      {/* Mobile slide-out menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute right-0 top-0 h-full w-72 bg-[#0e0e18] border-l border-purple/20 shadow-2xl flex flex-col animate-slide-in-right">
            {/* Drawer header */}
            <div className="flex items-center justify-between p-4 border-b border-purple/20">
              <span className="text-cream font-semibold text-sm">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)} className="text-cream/60 hover:text-cream p-1">
                <X size={20} />
              </button>
            </div>

            {/* User info */}
            {user && profile && (
              <Link to={profileLink} onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 border-b border-purple/10 hover:bg-purple/5 transition-colors">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt="Profile" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-purple/20 flex items-center justify-center">
                    <User size={18} className="text-cream/60" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-cream text-sm font-medium truncate">
                    {profile.display_name || profile.username}
                  </div>
                  <div className="text-cream/40 text-xs truncate">@{profile.username}</div>
                </div>
              </Link>
            )}

            {/* Nav links */}
            <nav className="flex-1 overflow-y-auto py-2">
              <div className="px-4 py-2">
                <label className="text-[10px] uppercase tracking-widest text-cream/30 font-semibold">Currency</label>
                <select
                  value={currency}
                  onChange={handleCurrencyChange}
                  className="mt-2 w-full h-9 rounded-md border border-purple/30 bg-dark px-2 py-1 text-xs text-cream"
                >
                  {supportedCurrencies.map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
              <div className="border-t border-purple/10 my-2" />
              <MobileNavLink to="/" label="Home" onClick={() => setMobileMenuOpen(false)} />
              <MobileNavLink to="/series" label="Series" onClick={() => setMobileMenuOpen(false)} />
              <MobileNavLink to="/sealed" label="Sealed products" onClick={() => setMobileMenuOpen(false)} />

              {user && (
                <>
                  <div className="border-t border-purple/10 my-2" />
                  <div className="px-4 py-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-cream/30 font-semibold">My Stuff</span>
                  </div>
                  <MobileNavLink to={collectionLink} icon={<Package size={16} />} label="My Collection" onClick={() => setMobileMenuOpen(false)} />
                  <MobileNavLink to={wishlistLink} icon={<Heart size={16} />} label="My Wishlist" onClick={() => setMobileMenuOpen(false)} />
                  <MobileNavLink to="/settings" icon={<Settings size={16} />} label="Settings" onClick={() => setMobileMenuOpen(false)} />
                </>
              )}
            </nav>

            {/* Footer */}
            <div className="border-t border-purple/20 p-4">
              {user ? (
                <button onClick={handleSignOut}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg text-sm transition-colors">
                  <LogOut size={16} />
                  Sign Out
                </button>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 bg-purple hover:bg-purple/90 text-white rounded-lg text-sm font-medium transition-colors justify-center">
                  <User size={16} />
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MobileNavLink({ to, label, icon, onClick }) {
  return (
    <Link to={to} onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 text-cream/80 hover:text-cream hover:bg-purple/5 transition-colors text-sm">
      {icon && <span className="text-cream/50">{icon}</span>}
      {label}
    </Link>
  );
}

export default Navbar;
